<!-- ============================================================
  SheetSessionColumn.vue — one class-session column of the grouping
  sheet (day mode: one column per session; week mode: stacked inside
  each day column). Header = time-slot label, then the session note
  row, the coach-subgroup blocks, the "ยังไม่จัดกลุ่ม" block, and a
  manager-only footer with "+ กลุ่ม".

  All mutations bubble up to GroupingSheet (single owner of dialogs
  and API calls): add-group / edit-group / edit-note / assign.
============================================================ -->
<template>
  <div class="gsc">
    <div class="tt-colhead gsc-head">
      <span class="strong row" style="gap:5px"><v-icon size="15">mdi-clock-outline</v-icon> {{ session.class_label }}</span>
      <span class="gsc-total">{{ totalStudents }} {{ $t('grouping.personUnit') }}</span>
    </div>

    <div v-if="session.note || !readonly" class="gsc-note" :class="{ clickable: !readonly, empty: !session.note }"
      @click="!readonly && $emit('edit-note', session)">
      <span class="mdi mdi-note-text-outline"></span>
      <span class="gsc-note-text">{{ session.note || $t('grouping.sessionNotePh') }}</span>
      <span v-if="!readonly" class="mdi mdi-pencil gsc-note-edit"></span>
    </div>

    <div class="gsc-blocks">
      <template v-for="g in visibleSubgroups" :key="g.subgroupid">
        <sheet-subgroup-block
          :subgroup="g"
          :students="g.members"
          :coach-map="coachMap"
          :readonly="readonly"
          :checkin-mode="checkinMode"
          :absent="isSubgroupAbsent(g)"
          :is-mine="!!g.isMine"
          :highlight-mine="highlightMine"
          :dimmed="highlightMine && myCoachId != null && !g.isMine"
          @edit="$emit('edit-group', { session, subgroup: g })"
          @assign="$emit('assign', { session, student: $event.student, currentSubgroupId: g.subgroupid, event: $event.event })"
          @checkin="$emit('checkin', $event)">
        </sheet-subgroup-block>
      </template>

      <sheet-subgroup-block v-if="showUnassigned"
        :subgroup="null"
        :students="session.unassigned"
        :coach-map="coachMap"
        :readonly="readonly"
        :checkin-mode="checkinMode"
        @assign="$emit('assign', { session, student: $event.student, currentSubgroupId: null, event: $event.event })"
        @checkin="$emit('checkin', $event)">
      </sheet-subgroup-block>

      <div v-if="session.subgroups.length === 0 && session.unassigned.length === 0" class="gsc-nobody">
        {{ $t('grouping.noBooking') }}
      </div>
    </div>

    <div v-if="!readonly" class="gsc-foot">
      <!-- hasRule = คอร์สนี้มีกฎจัดกลุ่มอัตโนมัติตั้งไว้ (backend ส่งมากับ session); คอร์สที่ไม่มีกฎ
           จะไม่เห็นปุ่มเลย ดีกว่าให้กดแล้วเด้ง error -->
      <button v-if="session.hasRule" type="button" class="id-btn id-btn-ghost gsc-autobtn" @click="$emit('auto-group', session)">
        <span class="mdi mdi-auto-fix"></span> {{ $t('grouping.autoBtn') }}
      </button>
      <button type="button" class="id-btn id-btn-ghost gsc-addbtn" @click="$emit('add-group', session)">
        <span class="mdi mdi-plus"></span> {{ $t('grouping.addGroup') }}
      </button>
    </div>
  </div>
</template>

<script>
import SheetSubgroupBlock from './SheetSubgroupBlock.vue'

export default {
  components: { SheetSubgroupBlock },
  props: {
    session: { type: Object, required: true },
    // absences of this session's date: [{ coachid, classtime, ... }] — classtime null = all day
    absences: { type: Array, default: () => [] },
    coachMap: { type: Object, default: () => ({}) },
    readonly: { type: Boolean, default: false },
    myCoachId: { type: Number, default: null },
    highlightMine: { type: Boolean, default: false },
    onlyMine: { type: Boolean, default: false },
    checkinMode: { type: Boolean, default: false },
  },
  emits: ['assign', 'edit-group', 'add-group', 'edit-note', 'checkin', 'auto-group'],
  computed: {
    totalStudents() {
      return this.session.subgroups.reduce((n, g) => n + g.members.length, 0) + this.session.unassigned.length
    },
    visibleSubgroups() {
      if (this.onlyMine && this.myCoachId != null) return this.session.subgroups.filter((g) => g.isMine)
      return this.session.subgroups
    },
    showUnassigned() {
      if (this.onlyMine && this.myCoachId != null) return false
      // ฝั่ง manager แสดงบล็อกนี้เสมอเมื่อมีกลุ่มแล้ว (ให้เห็นชัดว่าใครยังไม่มีกลุ่ม);
      // ฝั่งโค้ชแสดงเฉพาะเมื่อมีเด็กค้างจริง
      if (this.session.unassigned.length > 0) return true
      return !this.readonly && this.session.subgroups.length > 0
    },
    // คาบนี้อยู่ช่วงเช้าไหม (เริ่มก่อนเที่ยง) — ใช้จับคู่กับการลาครึ่งเช้า/ครึ่งบ่าย
    sessionIsMorning() {
      const m = String(this.session.classtime || '').match(/^(\d{1,2}):/)
      return m ? Number(m[1]) < 12 : true
    },
  },
  methods: {
    // โค้ชของกลุ่มลา "คาบนี้" ไหม — dayportion: full=ทั้งวัน, morning=ก่อนเที่ยง, afternoon=ตั้งแต่เที่ยง
    // (ข้อมูลเก่าที่ยังใช้ classtime: ตรงคาบพอดี = ลา)
    absenceHitsSession(a) {
      if (a.dayportion === 'morning') return this.sessionIsMorning
      if (a.dayportion === 'afternoon') return !this.sessionIsMorning
      if (a.dayportion === 'full' || !a.classtime) return true
      return a.classtime === this.session.classtime
    },
    isSubgroupAbsent(g) {
      return (g.coachids || []).some((id) =>
        this.absences.some((a) => a.coachid === id && this.absenceHitsSession(a)))
    },
  },
}
</script>

<style scoped>
.gsc { display: flex; flex-direction: column; }
.gsc-head { display: flex; align-items: center; justify-content: space-between; gap: 6px; }
.gsc-total { font-size: 11.5px; color: var(--c-text-muted); font-weight: 400; white-space: nowrap; }
.gsc-note { display: flex; align-items: flex-start; gap: 6px; font-size: 12px; padding: 6px 10px;
  border: 1px solid var(--c-border); border-top: none; background: var(--c-surface-2); color: var(--c-text-body); }
.gsc-note.clickable { cursor: pointer; }
.gsc-note.clickable:hover { background: var(--c-surface-3); }
.gsc-note.empty { color: var(--c-text-muted); font-style: italic; }
.gsc-note .mdi { font-size: 14px; }
.gsc-note-text { flex: 1; min-width: 0; overflow-wrap: anywhere; white-space: pre-wrap; }
.gsc-note-edit { opacity: 0.45; }
.gsc-blocks { display: flex; flex-direction: column; gap: 8px; padding: 8px; border: 1px solid var(--c-border);
  border-top: none; border-radius: 0 0 var(--radius-md, 10px) var(--radius-md, 10px); background: var(--c-surface); flex: 1; }
.gsc-nobody { font-size: 12px; color: var(--c-text-muted); text-align: center; padding: 14px 6px; }
.gsc-foot { padding: 6px 8px 0; display: flex; flex-direction: column; gap: 6px; }
.gsc-addbtn { width: 100%; font-size: 12.5px; border-style: dashed; justify-content: center; }
/* ปุ่มจัดกลุ่มอัตโนมัติ — เส้นทึบ + สีแบรนด์ ให้เด่นกว่าปุ่ม "เพิ่มกลุ่ม" (เส้นประ) ที่เป็นงานมือ */
.gsc-autobtn { width: 100%; font-size: 12.5px; justify-content: center; border-color: var(--c-primary); color: var(--c-primary); }
.gsc-autobtn:hover { background: var(--c-surface-3); }
</style>
